import React, { useState } from 'react';
import { Avatar, IconButton, Menu, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Booking, useCreateBooking, useDeleteBooking } from '../../hooks/api/booking';
import { Lesson, useDeleteLessons } from '../../hooks/api/lesson';
import './home.scss';
import { convertDateToTimeString } from '../../util/dates';
import MobileParticipantsList from './MobileParticipantsList';
import UpdateLessonModal from './UpdateLessonModal';
import { useCurrentUser } from '../../hooks/useCurrentUser';

interface LessonCardProps {
  lesson: Lesson;
  bookings: Booking[];
  userBooking?: Booking;
}

export default function LessonCard({ lesson, userBooking, bookings }: LessonCardProps) {
  const { currentUserId, isInstructor, user, isLoadingCurrentUser } = useCurrentUser();
  const [isUpdateLessonModalOpen, setIsUpdateLessonModalOpen] = useState(false);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const createBookingMutation = useCreateBooking();
  const deleteBookingMutation = useDeleteBooking();
  const deleteLessonMutation = useDeleteLessons();

  const isPastLesson = new Date() > new Date(lesson.startDate);

  const createBooking = (lessonId: number, date: string) => {
    if (!currentUserId) return;

    if (!user?.plan) {
      alert(
        'Lo sentimos, no has contratado ningún plan. Para seguir reservando ve a la página de pago y no te pierdas ningún entrenamiento',
      );
      return;
    }

    const lessonAccessLevel = lesson.accessLevel ?? 'normal';
    const allowedAccessLevels = user.plan.allowedLessonAccessLevels;

    if (!allowedAccessLevels.includes(lessonAccessLevel)) {
      alert('Lo sentimos, tu plan no permite reservar esta clase.');
      return;
    }

    createBookingMutation.mutate(
      { lessonId, userId: currentUserId },
      {
        onSuccess: () => {
          alert(`Has reservado la clase a las ${date}.`);
        },
        onError: () => {
          alert(
            'Lo sentimos, actualmente no cuentas con más clases en tu plan. Para seguir reservando ve a la página de pago y no te pierdas ningún entrenamiento',
          );
        },
      },
    );
  };

  const deleteBooking = (bookingId: number, date: string) => {
    deleteBookingMutation.mutate({ bookingId });
    alert(`Has cancelado la reserva de las ${date}.`);
  };

  const deleteLesson = (lessonId: number, date: string) => {
    deleteLessonMutation.mutate({ lessonId });
    alert(`Has cancelado la clase de las ${date}.`);
  };

  if (!currentUserId || isLoadingCurrentUser) return null;

  const startTime = convertDateToTimeString(new Date(lesson.startDate));
  const endTime = convertDateToTimeString(new Date(lesson.endDate));
  const professorName = `${lesson.professor.firstName} ${lesson.professor.lastName}`.trim();

  const seats = bookings.length;
  const occupancy = lesson.maxSeats > 0 ? Math.min((seats / lesson.maxSeats) * 100, 100) : 0;
  const isFull = seats >= lesson.maxSeats;

  const isProfessorOfLesson = currentUserId === lesson.professor.id;
  const showMemberCta = !isProfessorOfLesson && !isPastLesson;
  const showInstructorTools = isInstructor && !isPastLesson;

  return (
    <>
      {isInstructor && (
        <UpdateLessonModal
          isOpen={isUpdateLessonModalOpen}
          close={() => setIsUpdateLessonModalOpen(false)}
          initialValues={{
            startDate: lesson.startDate,
            endDate: lesson.endDate,
            type: lesson.type,
            accessLevel: lesson.accessLevel ?? 'normal',
            professorId: lesson.professor.id,
          }}
          lessonId={lesson.id}
        />
      )}

      <article className={`ll-card${isPastLesson ? ' ll-card--past' : ''}`}>
        <div className="ll-card__head">
          <div className="ll-headinfo">
            <div className="ll-time">
              <span className="ll-time__start">{startTime}</span>
              <span className="ll-time__end">– {endTime}</span>
            </div>
            <div className="ll-meta">
              <span className="ll-meta__type">{lesson.type}</span>
            </div>
          </div>
          <div className="ll-card__right">
            <Avatar
              className="ll-avatar"
              src={lesson.professor.image}
              alt={professorName}
              sx={{ width: 38, height: 38 }}
            />
            {showInstructorTools && (
              <IconButton
                size="small"
                className="ll-kebab"
                aria-label="Opciones de la clase"
                onClick={(event) => setMenuAnchor(event.currentTarget)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>

        <div className={`ll-cap${isFull ? ' ll-cap--full' : ''}`}>
          <span className="ll-cap__bar">
            <span style={{ width: `${occupancy}%` }} />
          </span>
          <span className="ll-cap__txt">
            {seats}/{lesson.maxSeats}
          </span>
        </div>

        {showMemberCta &&
          (userBooking ? (
            <button
              type="button"
              className="ll-cta ll-cta--cancel"
              onClick={() => deleteBooking(userBooking.id!, startTime)}
            >
              Cancelar reserva
            </button>
          ) : (
            <button
              type="button"
              className="ll-cta ll-cta--book"
              disabled={isFull}
              onClick={() => createBooking(lesson.id, startTime)}
            >
              {isFull ? 'Clase completa' : 'Reservar clase'}
            </button>
          ))}

        {isPastLesson && <div className="ll-finished">Clase finalizada</div>}

        <button
          type="button"
          className="ll-parts"
          onClick={() => setIsParticipantsModalOpen(true)}
        >
          <GroupsIcon />
          Ver participantes
        </button>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setIsUpdateLessonModalOpen(true);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1.25 }} />
            Editar
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              deleteLesson(lesson.id, startTime);
            }}
            sx={{ color: '#d32f2f' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1.25 }} />
            Eliminar
          </MenuItem>
        </Menu>
      </article>

      <MobileParticipantsList
        bookings={bookings}
        isOpen={isParticipantsModalOpen}
        closeModal={() => setIsParticipantsModalOpen(false)}
        lessonId={lesson.id}
        maxSeats={lesson.maxSeats}
      />
    </>
  );
}
